// chatClient — the web chat engine (chat-mls.md CH3). Owns the MLS session,
// the sync loop, and the stores; React renders chatStore snapshots and calls
// the exported verbs. Framework-free on purpose (§9.1), like call-engine.
//
// The crypto rules live elsewhere: protocol in rust/bz-mls, envelope routing
// in rules.ts. This file is choreography — enroll, replay, decrypt, ack —
// and its one hard invariant: the session blob is persisted after EVERY
// mutating MLS call, because a lost ratchet step is an unreadable message.

import { loadMls, type MlsSession } from '@brizina/mls-wasm';

import {
  appendOpenMessage,
  patchOpenMessage,
  resetChat,
  setChatConvs,
  setChatReady,
  setOpenConversation,
  updateOpenMessage,
  type ChatConvView,
  type ChatReactionView,
} from '../store/chatStore';
import { chatApi, ChatApiError, fromB64, type ConversationRow } from './chatApi';
import { chatDb, selectDatabase, type ConvMeta, type StoredMessage } from './chatDb';
import { envelopeAction, missingDevices } from './rules';
import {
  applyReaction,
  currentReaction,
  decodeMessage,
  encodeFile,
  encodeReaction,
  encodeText,
  reactionOps,
  type FileRef,
} from './wire';
import { decryptFile, encryptFile } from './fileCrypto';
import { getWithProgress, putWithProgress } from './xhr';

const SESSION_BLOB = 'sessionBlob';
const DEVICE_ID = 'deviceId';
/** Fresh one-time KeyPackages published at enroll / top-up (chat-mls.md §4). */
const POOL_FRESH = 20;
const POOL_TOPUP = 5;

let session: MlsSession | null = null;
let deviceId = '';
let currentUserId = '';
let starting: Promise<boolean> | null = null;
let syncing: Promise<void> | null = null;
let syncAgain = false;

function reactionViews(m: StoredMessage): ChatReactionView[] {
  return Object.entries(m.reactions ?? {}).map(([emoji, users]) => ({
    emoji,
    count: users.length,
    mine: users.includes(currentUserId),
  }));
}

function hex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function unhex(s: string): Uint8Array {
  const out = new Uint8Array(s.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(s.slice(i * 2, i * 2 + 2), 16);
  return out;
}

async function persistSession(): Promise<void> {
  if (!session) return;
  await chatDb.putKV(SESSION_BLOB, session.export());
}

async function publishKeyPackages(count: number, withLastResort: boolean): Promise<void> {
  if (!session) return;
  const packages = [];
  for (let i = 0; i < count; i++) {
    packages.push({ id: crypto.randomUUID(), data: session.keyPackage() });
  }
  if (withLastResort) {
    packages.push({ id: crypto.randomUUID(), data: session.keyPackage(), lastResort: true });
  }
  // Persist the key-package secrets BEFORE announcing them to the server. If the
  // tab is closed in the gap, the server would otherwise advertise key packages
  // the restored session no longer holds, and every welcome built from one fails
  // "key package not found" forever (the iOS device-log root cause).
  await persistSession();
  await chatApi.addKeyPackages(deviceId, packages);
}

async function enrollFresh(Mls: Awaited<ReturnType<typeof loadMls>>): Promise<void> {
  deviceId = crypto.randomUUID();
  session = Mls.create(deviceId);
  await chatApi.registerDevice(deviceId, session.signaturePublicKey);
  await chatDb.putKV(DEVICE_ID, deviceId);
  await persistSession();
  await publishKeyPackages(POOL_FRESH, true);
}

/**
 * Enroll this device and load the session for the SIGNED-IN user. Safe to
 * call repeatedly; runs once per account. Called at sign-in (PresenceGate)
 * so every web user is addressable even before they ever open Chat.
 * Resolves false when chat cannot run.
 */
export function ensureReady(userId: string): Promise<boolean> {
  if (userId !== currentUserId) {
    // Account switch in the same browser: drop the previous account's
    // in-memory state entirely. Each account has its own database, its own
    // device, its own session — sharing any of it means silent 403s.
    currentUserId = userId;
    session = null;
    deviceId = '';
    starting = null;
    syncing = null;
    syncAgain = false;
    resetChat();
    selectDatabase(userId);
  }
  starting ??= (async () => {
    try {
      const Mls = await loadMls();
      const storedDevice = await chatDb.getKV<string>(DEVICE_ID);
      const blob = await chatDb.getKV<Uint8Array>(SESSION_BLOB);
      if (storedDevice && blob) {
        deviceId = storedDevice;
        session = Mls.restore(blob);
        try {
          // Re-register (refreshes last_seen) and top the pool up.
          await chatApi.registerDevice(deviceId, session.signaturePublicKey);
          await publishKeyPackages(POOL_TOPUP, false);
        } catch (err) {
          if (err instanceof ChatApiError && err.status === 403) {
            // The stored device belongs to another account (pre-namespacing
            // residue). Never limp along with a foreign identity — start over.
            await enrollFresh(Mls);
          }
          // Other failures (network) keep the restored session: fully usable.
        }
      } else {
        await enrollFresh(Mls);
      }
      setChatReady(true);
      void sync();
      return true;
    } catch (err) {
      // The page renders one generic line; the console keeps the real reason
      // (a CSP refusing wasm looked identical to a network failure once).
      console.error('chat could not start', err);
      setChatReady(false, true);
      return false;
    }
  })();
  return starting;
}

/** The presence socket's "chat" ping lands here — a nudge, never content. */
export function onChatPing(): void {
  void sync();
}

/** Pull every conversation up to date. Coalesces concurrent calls. */
export async function sync(): Promise<void> {
  if (!session) return;
  if (syncing) {
    syncAgain = true;
    return syncing;
  }
  syncing = (async () => {
    try {
      const { conversations } = await chatApi.listConversations(deviceId);
      for (const row of conversations) {
        try {
          await syncConversation(row);
        } catch (err) {
          // One sick conversation must never starve the rest — and it must
          // never be silent (a swallowed error here once read as "message
          // lost" to the person waiting for it).
          console.warn('chat sync failed for conversation', row.id, err);
        }
      }
      await publishConvs();
    } catch (err) {
      console.warn('chat sync failed', err);
    } finally {
      syncing = null;
      if (syncAgain) {
        syncAgain = false;
        void sync();
      }
    }
  })();
  return syncing;
}

async function metaFor(row: ConversationRow): Promise<ConvMeta> {
  const existing = await chatDb.getConv(row.id);
  if (existing) return existing;
  // First sight of a conversation somebody else created: we are enrolled
  // server-side (the add-commit's metadata), and the welcome is on its way.
  const fresh: ConvMeta = {
    id: row.id,
    kind: row.kind,
    peerUserId: row.peerUserId,
    joined: false,
    joinSeq: 0,
    epoch: row.epoch,
    lastProcessedSeq: 0,
    lastReadSeq: 0,
  };
  await chatDb.putConv(fresh);
  return fresh;
}

/**
 * After a crash between sending a staged commit and confirming it, the group
 * holds a pending commit whose fate we don't know. Ask the server: if OUR
 * commit is the next in the stream it was accepted → confirm it; otherwise it
 * never won → roll back (normal sync then processes the winner).
 */
async function resolvePending(meta: ConvMeta): Promise<void> {
  if (!session || !meta.groupId) return;
  const groupId = unhex(meta.groupId);
  if (!session.hasPendingCommit(groupId)) return;
  let envs;
  try {
    envs = (await chatApi.listEnvelopes(meta.id, deviceId, meta.lastProcessedSeq)).envelopes;
  } catch {
    return; // can't reach the server — leave it pending, resolve next sync
  }
  const firstCommit = envs.find((e) => e.kind === 'commit');
  if (firstCommit && firstCommit.senderDevice === deviceId) {
    session.confirmCommit(groupId); // our staged commit was accepted
    meta.epoch += 1;
    meta.lastProcessedSeq = Math.max(meta.lastProcessedSeq, firstCommit.seq);
  } else {
    session.rollbackCommit(groupId); // ours never won — discard and rebase
  }
  await persistSession();
  await chatDb.putConv(meta);
}

async function syncConversation(row: ConversationRow): Promise<void> {
  if (!session) return;
  const meta = await metaFor(row);
  // Before processing anything, settle a commit left dangling by a crash —
  // otherwise the loop below would process messages at the wrong epoch.
  if (meta.joined) await resolvePending(meta);
  // Self-heal a stuck join: we ARE a member (the server returned this
  // conversation) but never joined it — the welcome join raced a fresh device's
  // busy enrolment and we acked PAST the welcome, wedging the thread at
  // "Connecting…". Rewind so this sync re-fetches the welcome and retries.
  // listEnvelopes filters by seq > afterSeq, not the ack cursor, so the welcome
  // is re-served while it still exists.
  if (!meta.joined && !meta.groupId && meta.lastProcessedSeq > 0) {
    meta.lastProcessedSeq = 0;
  }
  // Fetch new envelopes only when there ARE any — but reconciliation runs
  // below regardless, so a caught-up conversation still adds a missing
  // device (this was the bug: the early return skipped reconcile).
  if (row.maxSeq > meta.lastProcessedSeq) {
  const { envelopes, epoch } = await chatApi.listEnvelopes(row.id, deviceId, meta.lastProcessedSeq);
  let mutated = false;
  for (const env of envelopes) {
    const action = envelopeAction(
      { kind: env.kind, seq: env.seq, senderDevice: env.senderDevice },
      { myDevice: deviceId, joined: meta.joined, joinSeq: meta.joinSeq },
    );
    if (action === 'join') {
      try {
        const groupId = session.join(fromB64(env.payload));
        meta.groupId = hex(groupId);
        meta.joined = true;
        meta.joinSeq = env.seq;
        mutated = true;
      } catch (err) {
        // Join failed (a transient race during enrolment). Do NOT advance past
        // this welcome — stop so the next sync re-fetches it and retries,
        // instead of acking past it and wedging the thread at "Connecting…".
        console.warn('chat: welcome join failed, will retry', meta.id, env.seq, err);
        break;
      }
    } else if (action === 'process' && meta.groupId) {
      try {
        const result = session.process(unhex(meta.groupId), fromB64(env.payload));
        mutated = true;
        if (result.kind === 'message') {
          const wire = decodeMessage(result.data);
          if (wire.t === 'react') {
            // Total order says the target already arrived (or is our own
            // send, stored at send time) — a miss is a deleted/foreign seq.
            const target = await chatDb.getMessage(meta.id, wire.target);
            if (target) {
              target.reactions = applyReaction(target.reactions, wire.emoji, env.senderUser, wire.op);
              await chatDb.putMessage(target);
              updateOpenMessage(meta.id, target.seq, reactionViews(target));
            }
          } else {
            // Mine when ANY of my devices sent it — a message from my other
            // device is still mine, not the peer's (the bug this fixes).
            const isMine = env.senderUser === currentUserId;
            const isFile = wire.t === 'file';
            const file: FileRef | undefined = isFile
              ? {
                  objectKey: wire.objectKey,
                  name: wire.name,
                  size: wire.size,
                  mime: wire.mime,
                  nonce: wire.nonce,
                  key: wire.key,
                  hash: wire.hash,
                }
              : undefined;
            const text = isFile ? wire.name : wire.text;
            const msg: StoredMessage = {
              convId: meta.id,
              seq: env.seq,
              isMine,
              senderDevice: env.senderDevice,
              text,
              at: Date.parse(env.createdAt) || Date.now(),
              file,
            };
            await chatDb.putMessage(msg);
            appendOpenMessage(meta.id, { seq: msg.seq, isMine, text, at: msg.at, file });
          }
        } else if (result.kind === 'removed') {
          meta.removed = true;
        }
      } catch (err) {
        // Undecryptable envelope (pre-join stragglers, epoch skew). Skipping
        // is correct: FS means it will never become readable, and stalling
        // the cursor would wedge the conversation forever. But never silent.
        console.warn('chat: dropped undecryptable envelope', meta.id, env.seq, err);
      }
    }
    meta.lastProcessedSeq = env.seq;
  }
  meta.epoch = epoch;
  if (mutated) await persistSession();
  } else {
    // Nothing new to fetch — keep our epoch aligned with the server's for
    // the reconcile commit's parentEpoch.
    meta.epoch = row.epoch;
  }
  await chatDb.putConv(meta);
  // Multi-device: pull in any member-user device the group is missing (your
  // own second device, a late enrolment). Idempotent — once everyone is in,
  // this is one cheap GET that adds nothing.
  await reconcileRoster(meta);
  await chatApi.ack(deviceId, meta.id, meta.lastProcessedSeq).catch(() => undefined);
}

/**
 * Add every active member-user device the MLS group is missing (CH6). Only a
 * joined member can add, so this runs after we hold the group. Racing adds
 * from two devices resolve via the epoch gate; on a stale-epoch reject we
 * bail and the next sync retries.
 */
async function reconcileRoster(meta: ConvMeta): Promise<void> {
  if (!session || !meta.joined || !meta.groupId || meta.removed) return;
  let expected;
  try {
    expected = (await chatApi.roster(meta.id, deviceId)).devices;
  } catch {
    return; // roster is a nicety; never let it break the sync
  }
  const groupId = unhex(meta.groupId);
  // A pending commit left by an interrupted attempt would fork the group;
  // discard it and rebuild from the confirmed state.
  if (session.hasPendingCommit(groupId)) {
    session.rollbackCommit(groupId);
    await persistSession();
  }
  const present = session.members(groupId);
  const missing = missingDevices(expected, present, deviceId);
  for (const m of missing) {
    let pkg;
    try {
      pkg = await chatApi.claimDeviceKeyPackage(m.deviceId);
    } catch {
      continue; // no key package yet — try this device next sync
    }
    try {
      const ok = await stagedAdd(meta, groupId, {
        userId: m.userId,
        deviceId: m.deviceId,
        data: pkg.data,
      });
      if (!ok) return; // rejected — next sync rebases
    } catch {
      return; // any other failure — stop; next sync retries
    }
  }
}

async function publishConvs(): Promise<void> {
  const metas = await chatDb.allConvs();
  const views: ChatConvView[] = [];
  for (const m of metas) {
    const messages = await chatDb.messagesOf(m.id);
    const last = messages[messages.length - 1];
    // Hide only a NOT-YET-JOINED placeholder a peer created and nobody has
    // spoken in. Once we're a joined member the conversation shows even with
    // no local messages — being added to an existing conversation (multi-
    // device) leaves all its history pre-join and undecryptable, but the
    // conversation is real and must appear.
    if (!m.joined && !m.initiatedByMe && messages.length === 0) continue;
    views.push({
      id: m.id,
      kind: m.kind,
      peerUserId: m.peerUserId,
      unread: messages.filter((x) => !x.isMine && x.seq > m.lastReadSeq).length,
      lastText: last?.text,
      lastAt: last?.at,
      pending: !m.joined,
      removed: m.removed ?? false,
    });
  }
  views.sort((a, b) => (b.lastAt ?? 0) - (a.lastAt ?? 0));
  setChatConvs(views);
}

/** Open a thread: load its history and mark it read. A pending DM gets a
 * background repair attempt — opening it IS asking for it to work. */
export async function openConversation(convId: string): Promise<void> {
  void sync(); // opening IS asking "what did I miss" — never rely on pings alone
  const meta = await chatDb.getConv(convId);
  if (meta && !meta.joined && meta.kind === 'dm') {
    void repairPendingDM(meta)
      .then((repaired) => {
        if (repaired) setOpenConversation(null, []); // superseded id — reopen from the list
      })
      .catch(() => undefined);
  }
  const messages = await chatDb.messagesOf(convId);
  setOpenConversation(
    convId,
    messages.map((m) => ({
      seq: m.seq,
      isMine: m.isMine,
      text: m.text,
      at: m.at,
      reactions: reactionViews(m),
      file: m.file,
    })),
  );
  if (meta) {
    meta.lastReadSeq = meta.lastProcessedSeq;
    await chatDb.putConv(meta);
    await publishConvs();
  }
}

export function closeConversation(): void {
  setOpenConversation(null, []);
}

async function claimPeer(peerUserId: string) {
  try {
    return await chatApi.claimKeyPackages(peerUserId);
  } catch (err) {
    if (err instanceof ChatApiError && err.status === 404) {
      throw new Error('They have not set up chat on any device yet — they need to open Brizina once.');
    }
    throw err;
  }
}

/** Add every claimed peer device: one commit+welcome per device, epochs
 * advancing in lockstep with the server's gate. */
/**
 * Add ONE device the by-the-books way (RFC 9420 DS model): stage the commit
 * WITHOUT applying it, let the server decide the order, then confirm on accept
 * or roll back on rejection. Applying before the server confirmed was the fork
 * bug. Returns false when the server rejected (someone committed first) so the
 * caller stops and lets the next sync rebase.
 */
async function stagedAdd(
  meta: ConvMeta,
  groupId: Uint8Array,
  target: { userId: string; deviceId: string; data: string },
): Promise<boolean> {
  if (!session) return false;
  const staged = session.stageAddMember(groupId, fromB64(target.data));
  try {
    // Commit and welcome land atomically — no half-state for a crash.
    const res = await chatApi.appendEnvelope(meta.id, deviceId, {
      kind: 'commit',
      payload: staged.commit,
      parentEpoch: meta.epoch,
      addDevices: [{ userId: target.userId, deviceId: target.deviceId }],
      welcome: staged.welcome,
      welcomeRecipient: target.deviceId,
    });
    // Accepted: NOW apply it locally — our epoch matches the server's.
    session.confirmCommit(groupId);
    meta.epoch += 1;
    meta.lastProcessedSeq = Math.max(meta.lastProcessedSeq, res.seq);
    await persistSession();
    await chatDb.putConv(meta);
    return true;
  } catch (err) {
    // Rejected (stale epoch) or any failure: discard the staged commit so the
    // group stays on its confirmed state, and let the next sync rebase.
    session.rollbackCommit(groupId);
    await persistSession();
    if (err instanceof ChatApiError && err.status === 409) return false;
    throw err;
  }
}

async function addPeerDevices(
  meta: ConvMeta,
  peerUserId: string,
  claimed: { deviceId: string; data: string }[],
): Promise<void> {
  if (!session || !meta.groupId) return;
  const groupId = unhex(meta.groupId);
  for (const pkg of claimed) {
    const ok = await stagedAdd(meta, groupId, {
      userId: peerUserId,
      deviceId: pkg.deviceId,
      data: pkg.data,
    });
    if (!ok) return; // rejected — next sync rebases and retries
  }
}

/**
 * A pending DM is one waiting for a welcome. Usually the welcome is seconds
 * away; but if the conversation is a zombie (its only member was a replaced
 * device), no welcome can ever come. Ask the server again — it supersedes
 * zombies — and rebuild if it did. Resolves true when the DM became usable.
 */
async function repairPendingDM(meta: ConvMeta): Promise<boolean> {
  if (!session || !meta.peerUserId || meta.joined) return meta.joined;
  const requestedId = crypto.randomUUID();
  const row = await chatApi.createConversation(requestedId, deviceId, 'dm', meta.peerUserId);
  if (row.id === meta.id) {
    // Still the same conversation: we have (or will get) membership — the
    // welcome is genuinely in flight. Nudge a sync and keep waiting.
    void sync();
    return false;
  }
  // Superseded: the zombie is gone server-side. Rebuild as creator.
  const claimed = await claimPeer(meta.peerUserId);
  await chatDb.deleteMessagesOf(meta.id);
  await chatDb.deleteConv(meta.id);
  const fresh: ConvMeta = {
    id: row.id,
    kind: 'dm',
    peerUserId: meta.peerUserId,
    groupId: hex(session.createGroup()),
    joined: true,
    joinSeq: 0,
    epoch: 0,
    lastProcessedSeq: 0,
    lastReadSeq: 0,
    initiatedByMe: true,
  };
  await chatDb.putConv(fresh);
  await persistSession();
  await addPeerDevices(fresh, meta.peerUserId, claimed.packages);
  await publishConvs();
  return true;
}

/**
 * Start (or return) the DM with a user. The peer's KeyPackages are claimed
 * BEFORE anything is created, so an unenrolled peer costs nothing. A DM we
 * created earlier while the peer had no devices heals here: next open,
 * their devices are claimed and added. When the peer created the DM first
 * (the race), we wait for their welcome; a pending DM self-repairs.
 */
export async function startDM(peerUserId: string): Promise<string> {
  if (!session) throw new Error('chat is not ready');
  const existing = (await chatDb.allConvs()).find((c) => c.kind === 'dm' && c.peerUserId === peerUserId);
  if (existing) {
    if (!existing.joined) {
      const repaired = await repairPendingDM(existing);
      if (repaired) {
        const fresh = (await chatDb.allConvs()).find((c) => c.kind === 'dm' && c.peerUserId === peerUserId);
        return fresh?.id ?? existing.id;
      }
      return existing.id;
    }
    // Heal a half-created DM: we are its only member when the peer had no
    // enrolled device at creation time (or every add failed).
    if (existing.groupId && !existing.removed) {
      const roster = session.members(unhex(existing.groupId));
      if (roster.length <= 1) {
        await addPeerDevices(existing, peerUserId, (await claimPeer(peerUserId)).packages);
        await publishConvs();
      }
    }
    return existing.id;
  }

  const claimed = await claimPeer(peerUserId);
  const requestedId = crypto.randomUUID();
  const row = await chatApi.createConversation(requestedId, deviceId, 'dm', peerUserId);
  if (row.id !== requestedId) {
    // The peer created this DM first; our membership + welcome arrive with
    // their add-commit. Record it and let sync land the rest. (The packages
    // just claimed go unused — a rare race, and packages are replenished.)
    await metaFor({ ...row, peerUserId });
    await publishConvs();
    void sync();
    return row.id;
  }

  const groupId = session.createGroup();
  const meta: ConvMeta = {
    id: row.id,
    kind: 'dm',
    peerUserId,
    groupId: hex(groupId),
    joined: true,
    joinSeq: 0,
    epoch: 0,
    lastProcessedSeq: 0,
    lastReadSeq: 0,
    initiatedByMe: true,
  };
  await chatDb.putConv(meta);
  await persistSession();
  await addPeerDevices(meta, peerUserId, claimed.packages);
  await publishConvs();
  return meta.id;
}

/** Toggle a reaction on a message: my device already holds that emoji on
 * the target → remove, otherwise add. Travels E2EE like any message; the
 * server cannot tell a reaction from a text. */
export async function toggleReaction(convId: string, targetSeq: number, emoji: string): Promise<void> {
  if (!session) throw new Error('chat is not ready');
  const meta = await chatDb.getConv(convId);
  if (!meta?.joined || !meta.groupId || meta.removed) return;
  const target = await chatDb.getMessage(convId, targetSeq);
  if (!target) return;
  // One reaction per person: replacing sends a remove for the old emoji
  // first, so every device converges on the same single chip.
  const ops = reactionOps(currentReaction(target.reactions, currentUserId), emoji);

  for (const o of ops) {
    const ciphertext = session.encrypt(
      unhex(meta.groupId),
      encodeReaction(targetSeq, o.emoji, o.op),
    );
    await persistSession();
    const { seq } = await chatApi.appendEnvelope(convId, deviceId, {
      kind: 'application',
      payload: ciphertext,
    });
    target.reactions = applyReaction(target.reactions, o.emoji, currentUserId, o.op);
    meta.lastProcessedSeq = Math.max(meta.lastProcessedSeq, seq);
  }
  await chatDb.putMessage(target);
  updateOpenMessage(convId, targetSeq, reactionViews(target));
  await chatDb.putConv(meta);
  await chatApi.ack(deviceId, convId, meta.lastProcessedSeq).catch(() => undefined);
}

/** Share a file (Layer CH): encrypt under a fresh per-file key, upload the
 * ciphertext, then send the reference (key + nonce + hash inside the E2EE
 * message). The server only ever holds opaque ciphertext. */
export async function sendFile(convId: string, file: File): Promise<void> {
  if (!session) throw new Error('chat is not ready');
  const meta = await chatDb.getConv(convId);
  if (!meta?.joined || !meta.groupId || meta.removed) throw new Error('conversation is not writable');

  const mime = file.type || 'application/octet-stream';
  const isImage = mime.startsWith('image/');
  const clientId = crypto.randomUUID();
  const localUrl = isImage ? URL.createObjectURL(file) : undefined;

  // Optimistic: the bubble appears NOW, in a sending state, with a local
  // preview for images — before any encryption or upload.
  appendOpenMessage(convId, {
    seq: -1,
    clientId,
    isMine: true,
    text: file.name,
    at: Date.now(),
    status: 'sending',
    progress: 0,
    localUrl,
    file: { objectKey: '', name: file.name, size: file.size, mime, nonce: '', key: '', hash: '' },
  });

  try {
    const enc = await encryptFile(await file.arrayBuffer());
    const fileId = crypto.randomUUID();
    const { uploadUrl, objectKey } = await chatApi.fileUploadUrl(
      convId,
      deviceId,
      fileId,
      enc.ciphertext.byteLength,
    );
    await putWithProgress(uploadUrl, enc.ciphertext, (f) =>
      patchOpenMessage(convId, { clientId }, { progress: f }),
    );

    const ref: FileRef = {
      objectKey,
      name: file.name,
      size: file.size,
      mime,
      nonce: enc.nonce,
      key: enc.key,
      hash: enc.hash,
    };
    const ciphertext = session.encrypt(unhex(meta.groupId), encodeFile(ref));
    await persistSession();
    const { seq } = await chatApi.appendEnvelope(convId, deviceId, {
      kind: 'application',
      payload: ciphertext,
    });

    const msg: StoredMessage = {
      convId,
      seq,
      isMine: true,
      senderDevice: deviceId,
      text: file.name,
      at: Date.now(),
      file: ref,
    };
    await chatDb.putMessage(msg);
    meta.lastProcessedSeq = Math.max(meta.lastProcessedSeq, seq);
    meta.lastReadSeq = meta.lastProcessedSeq;
    await chatDb.putConv(meta);
    // Keep the local preview as the render source so the image doesn't
    // re-download after sending.
    if (localUrl) imageUrlCacheSet(objectKey, localUrl);
    patchOpenMessage(
      convId,
      { clientId },
      { seq, status: undefined, progress: undefined, file: ref },
    );
    await publishConvs();
    await chatApi.ack(deviceId, convId, meta.lastProcessedSeq).catch(() => undefined);
  } catch (err) {
    patchOpenMessage(convId, { clientId }, { status: 'failed', progress: undefined });
    throw err;
  }
}

/** Bridge so the send path can seed the ChatPage image cache with the local
 * preview URL (avoids a re-download of an image you just sent). Set by
 * ChatPage on mount. */
let imageUrlCacheSet: (objectKey: string, url: string) => void = () => {};
export function registerImageCacheSetter(fn: (objectKey: string, url: string) => void): void {
  imageUrlCacheSet = fn;
}

/** Fetch a shared file's ciphertext, verify it, decrypt, and hand back a Blob
 * to save. The key/nonce/hash came over the E2EE message. */
export async function downloadFile(
  convId: string,
  ref: FileRef,
  onProgress?: (fraction: number) => void,
): Promise<Blob> {
  const { downloadUrl } = await chatApi.fileDownloadUrl(convId, deviceId, ref.objectKey);
  const ciphertext = await getWithProgress(downloadUrl, (f) => onProgress?.(f));
  const plain = await decryptFile(ciphertext, ref.key, ref.nonce, ref.hash);
  return new Blob([plain as BlobPart], { type: ref.mime || 'application/octet-stream' });
}

/** Send one message. Local echo is the send result — our own envelope can
 * never be decrypted back (MLS senders don't keep their own message keys). */
export async function sendMessage(convId: string, text: string): Promise<void> {
  if (!session) throw new Error('chat is not ready');
  const trimmed = text.trim();
  if (!trimmed) return;
  const meta = await chatDb.getConv(convId);
  if (!meta?.joined || !meta.groupId || meta.removed) throw new Error('conversation is not writable');

  const ciphertext = session.encrypt(unhex(meta.groupId), encodeText(trimmed));
  await persistSession();
  const { seq } = await chatApi.appendEnvelope(convId, deviceId, {
    kind: 'application',
    payload: ciphertext,
  });

  const msg: StoredMessage = {
    convId,
    seq,
    isMine: true,
    senderDevice: deviceId,
    text: trimmed,
    at: Date.now(),
  };
  await chatDb.putMessage(msg);
  meta.lastProcessedSeq = Math.max(meta.lastProcessedSeq, seq);
  meta.lastReadSeq = meta.lastProcessedSeq;
  await chatDb.putConv(meta);
  appendOpenMessage(convId, { seq, isMine: true, text: trimmed, at: msg.at });
  await publishConvs();
  await chatApi.ack(deviceId, convId, meta.lastProcessedSeq).catch(() => undefined);
}
